import React, { useEffect } from 'react'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import { PrismicRichText, PrismicLink } from '@prismicio/react'

// Components
import EmblaCarousel from '../components/embla/emblaCarousel'
import Layout from '../components/layout'
import PastAuctions from '../components/pastAuctions'
import Seo from '../components/seo'
import SliceList from '../components/sliceList'

export const query = graphql`
  query Page($id: String) {
    prismicPage(id: { eq: $id }) {
      ...PrismicPageFragment
    }
  }
`

const PageHeader = ({ data }) => {
  let images = false
  if (data.page_image?.gatsbyImageData) {
    images = withArtDirection(getImage(data.page_image.gatsbyImageData), [
      {
        media: '(max-width: 768px)',
        image: getImage(data.page_image.thumbnails.mobile.gatsbyImageData)
      }
    ])
  }

  return (
    <div className='relative h-96 xl:h-80 overflow-hidden bg-blue-900 xl:rounded'>
      {data.page_image.gatsbyImageData && (
        <div className='absolute right-0 h-full w-full md:w-1/2'>
          <GatsbyImage className='h-full w-full object-cover' width='765' image={images} alt={data.page_image.alt || ''} loading='eager' />
          <div aria-hidden='true' className='absolute inset-0 h-full w-full bg-blue-900 bg-opacity-80 bg-gradient-to-t from-blue-900 md:bg-transparent md:bg-gradient-to-r' />
        </div>
      )}
      <div className='relative inset-0 flex h-full flex-col justify-center px-4 py-10 text-center md:w-1/2 md:text-left lg:mx-auto lg:w-full lg:px-16'>
        <h1 className='font-serif text-5xl uppercase text-blue-100 md:text-6xl'>{data.page_title.text ? data.page_title.text : data.title}</h1>
        {data.page_text.text && (
          <div className='my-6 max-w-4xl space-y-2 text-xl text-white'>
            <PrismicRichText field={data.page_text.richText} />
          </div>
        )}
        {data.page_button_link && data.page_button_text && (
          <PrismicLink className='button inline-block border bg-white text-xl text-blue-900 hover:opacity-80' href={data.page_button_link.url}>
            {data.page_button_text}
          </PrismicLink>
        )}
        {data.secondary_button_text && data.secondary_button_link && (
          <PrismicLink className='button button-brand ml-2 inline-block text-xl' href={data.secondary_button_link.url}>
            {data.secondary_button_text}
          </PrismicLink>
        )}
      </div>
    </div>
  )
}

const PageTemplate = ({ data, location }) => {
  const page = data.prismicPage
  const pageData = page.data

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash
      if (hash.includes('0fcfee0d-5fb3-4728-8bab-a66b4991d80b')) {
        const newURL = `/auction/${hash.replace('0fcfee0d-5fb3-4728-8bab-a66b4991d80b', '3443a151-0fed-49e4-a383-bcfcdf794b38')}`
        window.location.replace(newURL)
      } else if (hash.startsWith('#!/')) {
        // Redirect with full reload
        const newURL = `/auction/${hash}`
        window.location.replace(newURL) // replaces history and forces reload
      }
    }
  }, [])

  return (
    <Layout uid={page.uid}>
      {page.uid !== 'auction' && (
        <div className='component-header mx-auto w-full max-w-screen-xl text-white'>
          {pageData.page_header.length > 1 ? (
            <EmblaCarousel auto={false} arrows={true} delayLength={6000} dots={false} loop={true} data={pageData}>
              {pageData.page_header.map((item, index) => {
                return (
                  <div className='embla__slide' key={`carousel-${index}`}>
                    <PageHeader data={item} />
                  </div>
                )
              })}
            </EmblaCarousel>
          ) : (
            <PageHeader data={pageData.page_header[0]} />
          )}
        </div>
      )}
      {page.uid === 'past-auctions' ? <PastAuctions /> : <SliceList slices={pageData.body} location={location} />}
    </Layout>
  )
}

export default withPrismicPreview(PageTemplate)

export const Head = ({ data, location }) => {
  const pageData = data.prismicPage.data
  const { pathname } = location
  return (
    <>
      <Seo pathname={pathname} title={pageData.meta_title ? pageData.meta_title : pageData.title} description={pageData.meta_description} image={pageData.page_header[0]?.page_image?.url} />
    </>
  )
}
